import { useCallback, useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl'
import { Link as RouterLink, useNavigate } from '../lib/router'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import { PenTool, Search } from 'lucide-react'
import { AppShell, Crumb, HomeCrumb } from '../components/AppShell'
import { BasemapSwitch, MapCanvas, fitToFeatures } from '../components/MapCanvas'
import { EmptyState, Eyebrow, Mono, StatusPill, Surface } from '../components/primitives'
import { Segmented } from '../components/Segmented'
import { ApiError } from '../api/client'
import { useFarmers, useMyPlots, useMyPlotsGeoJson, usePrincipal } from '../api/hooks'
import type { PlotStatus } from '../theme'
import { STATUS_COLOURS, tokens } from '../theme'
import { ha, num } from '../lib/format'

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
const byStatus = (key: 'fill' | 'stroke') => [
  'match', ['get', 'status'],
  'passed', STATUS_COLOURS.passed[key],
  'flagged', STATUS_COLOURS.flagged[key],
  'rejected', STATUS_COLOURS.rejected[key],
  'excluded', STATUS_COLOURS.excluded[key],
  STATUS_COLOURS.flagged[key],
] as never

/**
 * Where a supplier's or a farmer's login starts: their own plots, wherever
 * they were filed — on a map and in a list, each opening on its own.
 *
 * A supplier also sees their farmers, and narrows to one. A farmer draws a
 * new field from here.
 */
export function ProducerHome({ kind }: { kind: 'supplier' | 'farmer' }) {
  const navigate = useNavigate()
  const { data: principal } = usePrincipal()
  const me = principal?.user
  const [status, setStatus] = useState<'all' | PlotStatus>('all')
  const [farmerId, setFarmerId] = useState<string | undefined>()
  const [q, setQ] = useState('')
  const [overlay, setOverlay] = useState<'map' | 'satellite'>('satellite')

  const filters = { status: status === 'all' ? undefined : status, farmer_id: farmerId }
  const { data: list, isLoading } = useMyPlots({ ...filters, q: q || undefined, limit: 200 })
  const { data: shapes, error: shapesError } = useMyPlotsGeoJson(filters)
  const { data: farmers = [] } = useFarmers({ limit: 200 })

  const whose = kind === 'supplier'
    ? me?.suppliers?.map((s) => s.name).join(', ')
    : me?.farmers?.map((f) => f.name || f.code).join(', ')

  // --- map ---------------------------------------------------------------
  const mapRef = useRef<MapLibreMap | null>(null)
  const [ready, setReady] = useState(false)
  const onReady = useCallback((map: MapLibreMap) => {
    mapRef.current = map
    map.addSource('mine', { type: 'geojson', data: EMPTY })
    map.addLayer({ id: 'mine-fill', type: 'fill', source: 'mine', paint: { 'fill-color': byStatus('fill') } })
    map.addLayer({ id: 'mine-line', type: 'line', source: 'mine', paint: { 'line-color': byStatus('stroke'), 'line-width': 1.5 } })
    map.on('click', 'mine-fill', (e) => {
      const id = e.features?.[0]?.properties?.id
      if (id) navigate(`/plots/${id}`)
    })
    map.on('mouseenter', 'mine-fill', () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', 'mine-fill', () => { map.getCanvas().style.cursor = '' })
    setReady(true)
  }, [navigate])
  const onRemove = useCallback(() => { mapRef.current = null; setReady(false) }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!ready || !map) return
    ;(map.getSource('mine') as GeoJSONSource | undefined)?.setData(shapes ?? EMPTY)
    fitToFeatures(map, shapes)
  }, [ready, shapes])

  const tooMany = shapesError instanceof ApiError && shapesError.status === 422
  const plots = list?.plots ?? []

  return (
    <AppShell crumbs={[<HomeCrumb key="a" />, <Crumb key="b">{kind === 'supplier' ? 'My farms' : 'My fields'}</Crumb>]}>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '440px 1fr', minHeight: 0 }}>
        <Box sx={{ overflowY: 'auto', px: 6, py: 6, borderRight: `1px solid ${tokens.color.ink200}` }}>
          <Eyebrow sx={{ mb: 2 }}>{whose || (kind === 'supplier' ? 'Supplier' : 'Farmer')}</Eyebrow>
          <Typography variant="h1" sx={{ mb: 2 }}>{kind === 'supplier' ? 'My farms' : 'My fields'}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
            {num(list?.total ?? 0)} {list?.total === 1 ? 'plot' : 'plots'}, with the result of the latest check on each.
          </Typography>

          {kind === 'farmer' && (
            <Button startIcon={<PenTool size={14} strokeWidth={1.5} />} onClick={() => navigate('/checks?mode=draw')} sx={{ mb: 4 }}>
              Draw a field
            </Button>
          )}

          {kind === 'supplier' && farmers.length > 0 && (
            <Surface sx={{ mb: 4, p: 0, maxHeight: 220, overflowY: 'auto' }}>
              {[{ id: undefined, label: 'All farmers', count: undefined as number | undefined }, ...farmers.map((f) => ({
                id: f.id as string | undefined, label: f.name ? `${f.name} · ${f.code ?? ''}` : f.code ?? '—', count: f.plot_count,
              }))].map((f) => (
                <Box
                  key={f.id ?? 'all'}
                  role="button"
                  onClick={() => setFarmerId(f.id)}
                  sx={{
                    display: 'flex', justifyContent: 'space-between', px: 3, py: 1.5, cursor: 'pointer',
                    backgroundColor: farmerId === f.id ? tokens.color.ink100 : 'transparent',
                    '&:hover': { backgroundColor: tokens.color.ink100 },
                  }}
                >
                  <Typography variant="body2">{f.label}</Typography>
                  {f.count !== undefined && <Mono sx={{ color: 'text.secondary' }}>{num(f.count)}</Mono>}
                </Box>
              ))}
            </Surface>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <Segmented
              ariaLabel="Status"
              value={status}
              onChange={(v) => setStatus(v as typeof status)}
              options={[
                { value: 'all', label: 'All' },
                { value: 'passed', label: 'Passed' },
                { value: 'flagged', label: 'Flagged' },
                { value: 'rejected', label: 'Rejected' },
              ]}
            />
            <TextField
              size="small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Plot_ID or Farmer_ID"
              inputProps={{ 'aria-label': 'Search plots' }}
              InputProps={{ startAdornment: <Search size={14} strokeWidth={1.5} style={{ marginRight: 8, opacity: 0.6 }} /> }}
            />
          </Box>

          {!isLoading && plots.length === 0 ? (
            <EmptyState
              title="No plots yet"
              body={kind === 'farmer' ? 'Draw your first field, or upload a file of them.' : 'Plots filed for your farmers appear here.'}
            />
          ) : (
            <Box>
              {plots.map((p) => (
                <Box
                  key={p.id}
                  component={RouterLink}
                  to={`/plots/${p.id}`}
                  sx={{
                    display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, px: 2, py: 2,
                    borderBottom: `1px solid ${tokens.color.ink100}`, color: 'inherit', textDecoration: 'none',
                    '&:hover': { backgroundColor: tokens.color.ink100 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Mono>{p.plot_ref ?? p.id.slice(0, 8)}</Mono>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }} noWrap>
                      {p.region_name}{p.farmer_id ? ` · ${p.farmer_id}` : ''}{p.area_ha != null ? ` · ${ha(p.area_ha)}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <StatusPill status={p.status as PlotStatus} />
                    {p.issue_count > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {num(p.issue_count)} {p.issue_count === 1 ? 'issue' : 'issues'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {(list?.total ?? 0) > plots.length && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2 }}>
                  Showing {num(plots.length)} of {num(list!.total)}. Search or filter to narrow.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ position: 'relative', minHeight: 0, display: 'flex' }}>
          <MapCanvas onReady={onReady} onRemove={onRemove} basemap={overlay}>
            <BasemapSwitch
              value={overlay}
              onChange={setOverlay}
              options={[{ value: 'map', label: 'Basemap' }, { value: 'satellite', label: 'Satellite' }]}
            />
          </MapCanvas>
          {tooMany && (
            <Alert severity="info" sx={{ position: 'absolute', top: 16, left: 16, right: 16 }}>
              Too many plots to draw at once. Pick a farmer or a status to narrow the map.
            </Alert>
          )}
        </Box>
      </Box>
    </AppShell>
  )
}
